import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  PaCompactMetricChip,
  PaEmptyState,
  PaStatusBadge,
} from "components/platform-admin";
import { platformRoleAccess } from "helpers/RolesPermission";
import { fetchPlatformTenantPaymentHistory } from "store/services/platformAdmin.services";
import {
  buildBillingAlerts,
  buildTenantBillingSummaryChips,
  formatBillingStateLabel,
  formatBillingTimestamp,
  formatParticipantLimitUsage,
  getBillingStateVariant,
  getBillingStatusBadge,
  getParticipantLimitVariant,
} from "./tenantBilling.utils";
import TenantPaymentHistorySection from "./TenantPaymentHistorySection";
import {
  formatSubscriptionStatusLabel,
  getSubscriptionStatusVariant,
} from "./tenantOverview.utils";

const DetailRow = ({ label, children }) => (
  <div className="flex flex-col gap-1 border-b border-gray-100 py-3 last:border-b-0 sm:flex-row sm:justify-between">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900 sm:text-right">{children}</dd>
  </div>
);

const AlertBanner = ({ alert }) => {
  const styles = {
    danger: "border-red-200 bg-red-50 text-red-800",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    neutral: "border-gray-200 bg-gray-50 text-gray-800",
  };

  return (
    <div className={`rounded-lg border px-4 py-3 ${styles[alert.variant] || styles.neutral}`}>
      <p className="text-sm font-medium">{alert.title}</p>
      {alert.message ? <p className="mt-1 text-xs opacity-90">{alert.message}</p> : null}
    </div>
  );
};

const TenantBillingTab = ({ tenant, onExtendTrial = null }) => {
  const canViewBilling = platformRoleAccess("platform.billing.view");
  const canManageBilling = platformRoleAccess("platform.billing.manage");
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(canViewBilling);
  const [paymentHistoryError, setPaymentHistoryError] = useState(null);

  const loadPaymentHistory = useCallback(async () => {
    if (!canViewBilling || !tenant?.id) {
      setPaymentHistoryLoading(false);
      return;
    }

    setPaymentHistoryLoading(true);
    setPaymentHistoryError(null);

    try {
      const data = await fetchPlatformTenantPaymentHistory(tenant.id);
      setPaymentHistory(data);
    } catch (err) {
      setPaymentHistory(null);
      setPaymentHistoryError(err?.message || "Failed to load payment history");
    } finally {
      setPaymentHistoryLoading(false);
    }
  }, [canViewBilling, tenant?.id]);

  useEffect(() => {
    loadPaymentHistory();
  }, [loadPaymentHistory]);

  const alerts = useMemo(() => buildBillingAlerts(tenant), [tenant]);
  const billingStatus = useMemo(() => getBillingStatusBadge(tenant), [tenant]);
  const summaryChips = useMemo(
    () => buildTenantBillingSummaryChips(tenant, paymentHistory),
    [tenant, paymentHistory]
  );

  if (!tenant) {
    return null;
  }

  const subscription = tenant.subscription || {};
  const billing = tenant.billing || {};
  const financial = tenant.financial || {};
  const participantLimit = billing.participant_limit || {};
  const flags = tenant.flags || {};
  const limitVariant = getParticipantLimitVariant(participantLimit);

  if (!billing.has_payment_settings) {
    return (
      <PaEmptyState
        title="No billing profile"
        message="This tenant has no payment settings record yet."
        description="Stripe subscription data will appear here once billing is configured."
      />
    );
  }

  return (
    <div className="space-y-6">
      {canViewBilling && summaryChips.length > 0 ? (
        <section className="flex flex-wrap gap-2">
          {summaryChips.map((chip) => (
            <PaCompactMetricChip
              key={chip.id}
              label={chip.label}
              value={chip.value}
              variant={chip.variant || "default"}
              hint={chip.hint}
            />
          ))}
        </section>
      ) : null}

      {alerts.length > 0 ? (
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {alerts.map((alert) => (
            <AlertBanner key={alert.id} alert={alert} />
          ))}
        </section>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-gray-900">Subscription</h2>
            <div className="flex flex-wrap gap-3">
              {canManageBilling && subscription.on_trial && onExtendTrial ? (
                <button
                  type="button"
                  onClick={onExtendTrial}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                >
                  Extend trial
                </button>
              ) : null}
              <Link
                to="/billing"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
              >
                Financial overview →
              </Link>
              {platformRoleAccess("platform.plans.manage") ||
              platformRoleAccess("platform.billing.view") ? (
                <Link
                  to="/stripe"
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                >
                  Stripe management →
                </Link>
              ) : null}
            </div>
          </div>
          <dl>
            <DetailRow label="Financial state">
              <PaStatusBadge
                label={formatBillingStateLabel(financial.billing_state)}
                variant={getBillingStateVariant(financial.billing_state)}
              />
            </DetailRow>
            <DetailRow label="Plan">{subscription.plan_name || "—"}</DetailRow>
            <DetailRow label="Status">
              <PaStatusBadge label={billingStatus.label} variant={billingStatus.variant} />
            </DetailRow>
            <DetailRow label="Stripe subscription ID">
              <span className="font-mono text-xs">{subscription.subscription_id || "—"}</span>
            </DetailRow>
            <DetailRow label="Stripe customer ID">
              <span className="font-mono text-xs">{subscription.stripe_customer_id || "—"}</span>
            </DetailRow>
            <DetailRow label="Trial">
              {subscription.on_trial ? (
                <PaStatusBadge label="On trial" variant="warning" />
              ) : (
                "Not on trial"
              )}
            </DetailRow>
            {subscription.trial_start_date ? (
              <DetailRow label="Trial started">
                {formatBillingTimestamp(subscription.trial_start_date)}
              </DetailRow>
            ) : null}
            {subscription.trial_end_date ? (
              <DetailRow label="Trial ends">
                {formatBillingTimestamp(subscription.trial_end_date)}
              </DetailRow>
            ) : null}
            {subscription.stripe_dashboard_customer_url ? (
              <DetailRow label="Stripe customer">
                <a
                  href={subscription.stripe_dashboard_customer_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 hover:text-indigo-700"
                >
                  Open in Stripe
                </a>
              </DetailRow>
            ) : null}
            {subscription.stripe_dashboard_subscription_url ? (
              <DetailRow label="Stripe subscription">
                <a
                  href={subscription.stripe_dashboard_subscription_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 hover:text-indigo-700"
                >
                  Open in Stripe
                </a>
              </DetailRow>
            ) : null}
          </dl>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-gray-900">Payment recovery</h2>
          <dl>
            <DetailRow label="Billing status">
              {subscription.current_status ? (
                <PaStatusBadge
                  label={formatSubscriptionStatusLabel(subscription)}
                  variant={getSubscriptionStatusVariant(subscription, flags)}
                />
              ) : (
                "—"
              )}
            </DetailRow>
            <DetailRow label="Next payment attempt">
              {formatBillingTimestamp(subscription.next_payment_attempt || billing.next_payment_attempt)}
            </DetailRow>
            <DetailRow label="Attempt count">
              {subscription.attempt_count ?? billing.attempt_count ?? "—"}
            </DetailRow>
            <DetailRow label="Last payment error">
              {subscription.payment_error || billing.payment_error || "—"}
            </DetailRow>
          </dl>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-gray-900">Participant limits</h2>
          <dl>
            <DetailRow label="Active participants">
              <PaStatusBadge
                label={formatParticipantLimitUsage(participantLimit)}
                variant={limitVariant}
              />
            </DetailRow>
            <DetailRow label="Plan limit">
              {participantLimit.plan_limit ?? "Not available"}
            </DetailRow>
            <DetailRow label="Over limit">
              {participantLimit.over_limit ? (
                <PaStatusBadge label="Yes" variant="warning" />
              ) : (
                "No"
              )}
            </DetailRow>
            <DetailRow label="Limit exceeded at">
              {formatBillingTimestamp(
                participantLimit.exceeded_at || billing.participant_limit_exceeded_at
              )}
            </DetailRow>
            <DetailRow label="Days until read-only">
              {participantLimit.read_only_due_to_participant_limit
                ? "Already read-only"
                : participantLimit.days_until_read_only ?? "—"}
            </DetailRow>
            <DetailRow label="Limit read-only flag">
              {billing.participant_limit_read_only || flags.participant_limit_read_only ? (
                <PaStatusBadge label="Enabled" variant="danger" />
              ) : (
                "Off"
              )}
            </DetailRow>
          </dl>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-gray-900">Commercial & integrations</h2>
          <dl>
            <DetailRow label="Wildcard billing">
              {flags.wildcard ? (
                <PaStatusBadge label="Enabled" variant="warning" />
              ) : (
                "Standard"
              )}
            </DetailRow>
            {financial.wildcard_expiry ? (
              <DetailRow label="Wildcard expiry">
                {formatBillingTimestamp(financial.wildcard_expiry)}
              </DetailRow>
            ) : null}
            <DetailRow label="Manual read-only">
              {billing.manual_read_only ? (
                <PaStatusBadge label="Enabled" variant="warning" />
              ) : (
                "Off"
              )}
            </DetailRow>
            <DetailRow label="Effective read-only">
              {flags.read_only ? (
                <PaStatusBadge label="Tenant is read-only" variant="danger" />
              ) : (
                "Writable"
              )}
            </DetailRow>
            {financial.xero_subscription_id ? (
              <DetailRow label="Xero subscription">
                <span className="font-mono text-xs">{financial.xero_subscription_id}</span>
              </DetailRow>
            ) : null}
            {financial.xero_subscription_status ? (
              <DetailRow label="Xero status">{financial.xero_subscription_status}</DetailRow>
            ) : null}
          </dl>
        </section>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <TenantPaymentHistorySection
          tenantId={tenant.id}
          history={paymentHistory}
          loading={paymentHistoryLoading}
          error={paymentHistoryError}
          onRetry={loadPaymentHistory}
          onHistoryChange={setPaymentHistory}
        />
      </section>
    </div>
  );
};

export default TenantBillingTab;
